import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import { Form, FormInstance, Input, InputRef, Button, Space, Typography } from "antd";
import React from "react";

const { Text } = Typography;

interface Props {
    name: number;
    form: FormInstance<object>;
    draftData?: any;
    onDraftChange?: (newData: any) => void;
}

export const AnalyticEventsName: React.FC<Props> = ({ name, form, draftData, onDraftChange }) => {
    // Always call hooks first
    const formEventName = Form.useWatch(["analytic_events", name, "event_name"]);
    const formIsNew = Form.useWatch(["analytic_events", name, "is_new"]);
    const formIsEditing = Form.useWatch(["analytic_events", name, "is_editing"]);

    // Then use draft data if available
    const event_name = draftData ? draftData.event_name : formEventName;
    const is_new = draftData ? draftData.is_new : formIsNew;
    const is_editing = draftData ? draftData.is_editing : formIsEditing;

    const [isEditing, setIsEditing] = React.useState(Boolean(is_new || is_editing));

    React.useEffect(() => {
        setIsEditing(Boolean(is_new || is_editing));
    }, [is_new, is_editing]);

    const editInputRef = React.useRef<InputRef>(null);
    React.useEffect(() => {
        if (isEditing) {
            setTimeout(() => editInputRef.current?.focus(), 100);
        }
    }, [isEditing]);

    const handleSave = () => {
        if (event_name?.trim()) {
            setIsEditing(false);
            form.setFieldsValue({
                analytic_events: {
                    [name]: {
                        is_new: false,
                        is_editing: false,
                    },
                },
            });
        }
        form.validateFields([["analytic_events", name, "event_name"]]);
    };

    const handleCancel = () => {
        setIsEditing(false);
        form.setFieldValue(["analytic_events", name, "is_editing"], false);
    };

    if (!isEditing && !event_name) {
        return null; // Don't render anything if not editing and no name
    }

    return (
        <div className="mb-4">
            <Text strong className="block mb-2">
                Event Name
            </Text>
            {isEditing ? (
                <div className="flex items-center space-x-2">
                    <Form.Item
                        name={["analytic_events", name, "event_name"]}
                        rules={[{ required: true, message: "Event name is required" }]}
                        className="mb-0 flex-1"
                    >
                        <Input
                            ref={editInputRef}
                            placeholder="Enter event name (e.g., user_signup, level_complete)"
                            size="middle"
                            value={event_name}
                            onChange={(e) => {
                                if (draftData && onDraftChange) {
                                    onDraftChange({
                                        ...draftData,
                                        event_name: e.target.value,
                                    });
                                } else {
                                    form.setFieldValue(["analytic_events", name, "event_name"], e.target.value);
                                }
                            }}
                            onPressEnter={handleSave}
                            className="font-mono"
                        />
                    </Form.Item>
                    <Space>
                        <Button type="primary" size="small" icon={<CheckOutlined />} onClick={handleSave}>
                            Save
                        </Button>
                        <Button size="small" icon={<CloseOutlined />} onClick={handleCancel}>
                            Cancel
                        </Button>
                    </Space>
                </div>
            ) : (
                <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded">
                    <code className="text-blue-800 font-medium">{event_name}</code>
                    <Button
                        type="text"
                        size="small"
                        onClick={() => {
                            setIsEditing(true);
                            form.setFieldValue(["analytic_events", name, "is_editing"], true);
                        }}
                        className="text-blue-600"
                    >
                        Edit Name
                    </Button>
                </div>
            )}
        </div>
    );
};
